import { MouseEventHandler } from 'react';
import { Link, Toggle, Tooltip } from '@ets/design-system';
import { useIntl } from 'react-intl';
import { useSelector } from '_common/hooks';
import { LikesTooltip } from 'Editor/components';
import { LikeParent } from '_common/modals/LikesModal/LikesModal';

import styles from './LikeToggle.module.scss';
import { openModal } from 'App/ModalContext/utils';

export type LikeToggleProps = {
  votes: Card.Vote[];
  replyId?: Card.Reply['id'];
  target: LikeParent;
  onVote: (payload: { replyId?: string; currentUserLiked: boolean }) => void;
  disabled?: boolean;
  legacyModal?: boolean;
  testId: string;
  renderWrapper?: (children: React.ReactElement) => React.ReactElement;
};

const LikeToggle = ({
  votes,
  replyId,
  target,
  onVote,
  disabled,
  legacyModal,
  testId,
  renderWrapper,
}: LikeToggleProps) => {
  const currentUserId = useSelector((state) => state.auth.userId);
  const intl = useIntl();

  function getVoteUser(vote: Editor.Data.Vote): string {
    if (vote.user) return vote.user;
    else if (vote.username) return vote.username;
    return '';
  }

  const likedVotes: Card.Vote[] = votes
    .filter((vote) => vote.value)
    .map((vote) => ({
      time: '',
      user: getVoteUser(vote),
      value: vote.value,
    }));

  const currentUserLiked = !!likedVotes.find((vote) => vote?.user === currentUserId);

  const handleLikeReply: MouseEventHandler<HTMLSpanElement> = (e) => {
    e.stopPropagation();

    onVote({ replyId, currentUserLiked });
  };

  const ReuseToggle = (
    <Tooltip
      visible={disabled}
      content={intl.formatMessage({ id: 'RESOLVED_COMMENT_ACTIONS' })}
      testId={`${testId}-like-toggle`}
    >
      <Toggle
        size="m"
        variant="link"
        iconProps={{
          icon: 'thumbs-up',
        }}
        isToggled={currentUserLiked}
        testId={`${testId}-like`}
        onClick={handleLikeReply}
        disabled={disabled}
      />
    </Tooltip>
  );

  return (
    <div className={styles.likeCounter}>
      {renderWrapper ? renderWrapper(ReuseToggle) : ReuseToggle}
      {likedVotes.length > 0 && (
        <LikesTooltip votes={likedVotes} testId={`${testId}-likes`}>
          <span className={styles.counter}>
            <Link
              size="m"
              variant="standard"
              onClick={() =>
                openModal({
                  modal: 'TotalUsersModal',
                  data: {
                    users: likedVotes
                      .sort((a, b) => new Date(a.time).getDate() - new Date(b.time).getDate())
                      .map((vote) => getVoteUser(vote)),
                    elementType: target,
                    action: 'likes',
                    legacy: legacyModal,
                  },
                })
              }
              testId={`${testId}-likes`}
            >
              {likedVotes.length}
            </Link>
          </span>
        </LikesTooltip>
      )}
    </div>
  );
};

export default LikeToggle;
